import { X } from '@untitledui/icons';
import { useCallback, useMemo, useState } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { Select } from '@/lib/base/forms/select/select';
import { getPortfolioActionError } from '@/lib/portfolio/errors';
import { useAssignTeam } from '@/lib/portfolio/hooks/useAssignTeam';
import { useRemoveTeam } from '@/lib/portfolio/hooks/useRemoveTeam';
import type { PortfolioTeam } from '@/lib/portfolio/types';

interface MemberTeamsControlProps {
  portfolioId: number;
  orgId: number;
  userId: number;
  memberTeamIds: readonly string[];
  allTeams: readonly PortfolioTeam[];
}

interface AssignedTeamChipProps {
  team: PortfolioTeam;
  onRemove: (teamEntityId: string) => void;
  isPending: boolean;
}

function AssignedTeamChip({
  team,
  onRemove,
  isPending,
}: AssignedTeamChipProps) {
  const handleRemove = useCallback(() => {
    onRemove(team.entity_id);
  }, [onRemove, team.entity_id]);

  return (
    <Badge size='sm'>
      <span className='flex items-center gap-1'>
        {team.name}
        <Button
          color='tertiary'
          size='xs'
          iconLeading={X}
          onClick={handleRemove}
          isDisabled={isPending}
          aria-label={`Remove ${team.name}`}
        />
      </span>
    </Badge>
  );
}

export function MemberTeamsControl({
  portfolioId,
  orgId,
  userId,
  memberTeamIds,
  allTeams,
}: MemberTeamsControlProps) {
  const assignMutation = useAssignTeam(portfolioId, orgId);
  const removeMutation = useRemoveTeam(portfolioId, orgId);
  const [lastAction, setLastAction] = useState<'assign' | 'remove' | null>(
    null
  );

  const assignedTeams = useMemo(
    () => allTeams.filter(team => memberTeamIds.includes(team.entity_id)),
    [allTeams, memberTeamIds]
  );

  const assignableTeams = useMemo(
    () => allTeams.filter(team => !memberTeamIds.includes(team.entity_id)),
    [allTeams, memberTeamIds]
  );

  const assignableItems = useMemo(
    () =>
      assignableTeams.map(team => ({ id: team.entity_id, label: team.name })),
    [assignableTeams]
  );

  const handleRemove = useCallback(
    (teamEntityId: string) => {
      setLastAction('remove');
      assignMutation.reset();
      removeMutation.mutate({ userId, teamEntityId });
    },
    [assignMutation, removeMutation, userId]
  );

  const handleAssign = useCallback(
    (key: string | number | null) => {
      if (key == null) {
        return;
      }
      setLastAction('assign');
      removeMutation.reset();
      assignMutation.mutate({ userId, teamEntityId: String(key) });
    },
    [assignMutation, removeMutation, userId]
  );

  let erroredMutation = null;
  if (lastAction === 'assign' && assignMutation.isError) {
    erroredMutation = assignMutation;
  } else if (lastAction === 'remove' && removeMutation.isError) {
    erroredMutation = removeMutation;
  }

  return (
    <div>
      <div className='flex flex-wrap items-center gap-1.5'>
        {assignedTeams.map(team => (
          <AssignedTeamChip
            key={team.entity_id}
            team={team}
            onRemove={handleRemove}
            isPending={removeMutation.isPending}
          />
        ))}
        <Select
          size='sm'
          items={assignableItems}
          // Controlled with a null selection so the picker always reads as an
          // "Add team" action: on a failed assignment it never sticks showing
          // the failed team, and the same team can be retried after an error.
          selectedKey={null}
          placeholder={
            assignableTeams.length === 0 ? 'No teams available' : 'Add team'
          }
          isDisabled={assignableTeams.length === 0 || assignMutation.isPending}
          onSelectionChange={handleAssign}
          aria-label='Add team for member'
        >
          {item => <Select.Item id={item.id}>{item.label}</Select.Item>}
        </Select>
      </div>
      {erroredMutation && (
        <p
          role='alert'
          className='mt-1 text-body-small text-[var(--color-text-error-primary)]'
        >
          {getPortfolioActionError(erroredMutation.error)}
        </p>
      )}
    </div>
  );
}
